/**
 * Unified ContentService — single cache.
 *
 * Unified cache for CloudFront signed URLs, keyed by (namespace, logicalKey,
 * variant). It honors BOTH a fixed TTL and the handle's own expiresAt
 * (CloudFront Expires / S3 presign expiry), and supports an in-memory LRU for
 * stream handles plus sessionStorage persistence for signed URLs.
 *
 * Backwards-compatible with the existing gallery semantics: a signed URL carries
 * a CloudFront Expires query param; we cache it until that expiry minus a
 * safety margin rather than capping it at a short safety TTL.
 */

export const DEFAULT_TTL_MS = 4 * 60 * 1000;
const SAFETY_MARGIN_MS = 30 * 1000;
const SESSION_KEY = 'mgv.contentService.cache.v1';
const LRU_MAX = 2000;

/** Read the persistent map from an injected Storage-like object. */
function readFromStorage(storage) {
  if (!storage) return {};
  try {
    const raw = storage.getItem(SESSION_KEY);
    if (!raw) return {};
    const data = JSON.parse(raw);
    return data && typeof data === 'object' ? data : {};
  } catch {
    return {};
  }
}

/** Write the persistent map to an injected Storage-like object. */
function writeToStorage(storage, data) {
  if (!storage) return;
  try {
    storage.setItem(SESSION_KEY, JSON.stringify(data));
  } catch {
    /* quota */
  }
}

/** Parse CloudFront `Expires` (Unix seconds) from a URL → ms epoch, or null. */
export function parseExpires(url) {
  try {
    const u = new URL(String(url || ''), 'https://x.invalid');
    const raw = u.searchParams.get('Expires');
    if (!raw) return null;
    const secs = Number(raw);
    if (!Number.isFinite(secs) || secs <= 0) return null;
    return secs * 1000;
  } catch {
    return null;
  }
}

/** True when a URL (e.g. signed CloudFront) is already expired / within margin. */
export function isExpired(url, marginMs = SAFETY_MARGIN_MS) {
  const exp = parseExpires(url);
  if (exp == null) return false;
  return Date.now() >= exp - marginMs;
}

function persistentAvailable() {
  return typeof sessionStorage !== 'undefined';
}

function defaultReadStore() {
  if (!persistentAvailable()) return {};
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return {};
    const data = JSON.parse(raw);
    return data && typeof data === 'object' ? data : {};
  } catch {
    return {};
  }
}

function defaultWriteStore(data) {
  if (!persistentAvailable()) return;
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
  } catch {
    /* quota */
  }
}

/**
 * @param {object} [opts]
 * @param {number} [opts.defaultTtlMs]
 * @param {Storage} [opts.storage]        injectable store (for tests).
 * @param {() => Record<string,any>} [opts.readStore]
 * @param {(data:Record<string,any>) => void} [opts.writeStore]
 */
export class ContentCache {
  constructor(opts = {}) {
    this.defaultTtlMs = opts.defaultTtlMs ?? DEFAULT_TTL_MS;
    this._memory = new Map();
    this._storage = opts.storage ?? null;
    // When an explicit Storage-like object is injected, use it for persistence;
    // otherwise fall back to the default sessionStorage-backed read/write.
    this._readStore = opts.readStore ?? (this._storage ? () => readFromStorage(this._storage) : defaultReadStore);
    this._writeStore = opts.writeStore ?? (this._storage ? (d) => writeToStorage(this._storage, d) : defaultWriteStore);
  }

  static cacheKey(namespace, logicalKey, variant) {
    return [namespace, logicalKey, variant || '*'].join('|');
  }

  /** Effective expiry: prefer the URL's own Expires (minus margin) over TTL. */
  static effectiveExpiry(url, ttlMs) {
    const cf = parseExpires(url);
    if (cf != null) return cf - SAFETY_MARGIN_MS;
    return Date.now() + ttlMs;
  }

  get(namespace, logicalKey, variant) {
    const key = ContentCache.cacheKey(namespace, logicalKey, variant);
    const now = Date.now();

    const mem = this._memory.get(key);
    if (mem && mem.expires > now && !isExpired(mem.url)) {
      this._touch(key, mem);
      return mem.url;
    }

    const store = this._readStore();
    const row = store[key];
    if (row && row.url && row.expires > now && !isExpired(row.url)) {
      this._memory.set(key, row);
      this._touch(key, row);
      return row.url;
    }
    return null;
  }

  set(namespace, logicalKey, variant, url, handleTtlMs = this.defaultTtlMs, extra = {}) {
    const key = ContentCache.cacheKey(namespace, logicalKey, variant);
    const u = String(url || '').trim();
    if (!key || !u) return;
    const expires = ContentCache.effectiveExpiry(u, handleTtlMs);
    const row = {
      url: u,
      isStream: Boolean(extra.isStream),
      source: extra.source || 'unknown',
      expiresAt: extra.expiresAt,
      expires: Math.max(expires, Date.now()),
    };
    this._memory.set(key, row);
    this._touch(key, row);

    const store = this._readStore();
    store[key] = row;
    this._writeStore(store);
  }

  /**
   * Like `get`, but returns the full handle ({ url, isStream, source,
   * expiresAt, expires }) so callers preserve metadata across cache hits.
   * Returns null on miss/expiry.
   */
  getHandle(namespace, logicalKey, variant) {
    const key = ContentCache.cacheKey(namespace, logicalKey, variant);
    const now = Date.now();

    const mem = this._memory.get(key);
    if (mem && mem.expires > now && !isExpired(mem.url)) {
      this._touch(key, mem);
      return mem;
    }

    const store = this._readStore();
    const row = store[key];
    if (row && row.url && row.expires > now && !isExpired(row.url)) {
      this._memory.set(key, row);
      this._touch(key, row);
      return row;
    }
    return null;
  }

  /** Targeted eviction: drop the entry whose URL matches (reverse lookup). */
  evict(url) {
    const u = String(url || '').trim();
    if (!u) return;
    for (const [k, row] of this._memory.entries()) {
      if (row.url === u) {
        this._memory.delete(k);
        break;
      }
    }
    const store = this._readStore();
    for (const k of Object.keys(store)) {
      if (store[k] && store[k].url === u) {
        delete store[k];
        this._writeStore(store);
        break;
      }
    }
  }

  clear() {
    this._memory.clear();
    if (this._storage) {
      this._storage.removeItem(SESSION_KEY);
      return;
    }
    const store = this._readStore();
    for (const k of Object.keys(store)) delete store[k];
    this._writeStore(store);
  }

  _touch(key, row) {
    // LRU: recent keys move to the end. Trim when over capacity.
    this._memory.delete(key);
    this._memory.set(key, row);
    if (this._memory.size > LRU_MAX) {
      const oldest = this._memory.keys().next().value;
      if (oldest != null) this._memory.delete(oldest);
    }
  }
}

let _defaultCache = null;
export function getDefaultContentCache() {
  if (!_defaultCache) _defaultCache = new ContentCache();
  return _defaultCache;
}
